import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Box,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Pagination,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PrintIcon from "@mui/icons-material/Print";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useDelete from "../../../../api/useDelete";
import PrescriptionDetailsEdit from "./PrescriptionDetailsEdit";
import { useTranslation } from "react-i18next";
import PrescriptionPrint from "./PrescriptionPrint";
import { useNavigate } from "react-router-dom";

const PrescriptionList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1); // One-based page index
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPrescriptions, setTotalPrescriptions] = useState(0); // Total count

  const { handleDelete: handleDeletePrescription, ConfirmDialog } = useDelete(
    "prescriptions",
    token
  );
  const {
    handleDelete: handleDeletePrescriptionItem,
    ConfirmDialog: ConfirmDialogItem,
  } = useDelete("prescription-details", token);

  const { data, isLoading, isError, isSuccess, error, refetch } =
    useFetchObjects(
      ["prescriptions"],
      `prescriptions/?doctor=${auth?.user?.user?.id}&search=${searchQuery}&page=${page}&page_size=${rowsPerPage}`,
      token
    );

  useEffect(() => {
    refetch();
  }, [refetch, searchQuery, page, rowsPerPage]);

  useEffect(() => {
    if (data && data.count !== undefined) {
      setTotalPrescriptions(data.count);
    }
  }, [data]);

  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef();

  const handleOpen = (item) => {
    setSelectedItem(item);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
  };

  const handlePrintClick = (prescription) => {
    setPrintData(prescription);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case 1:
        return t("pharmacy.pending");
      case 2:
        return t("pharmacy.paid");
      case 3:
        return t("pharmacy.canceled");
      default:
        return t("pharmacy.unknown");
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 1:
        return "warning";
      case 2:
        return "success";
      case 3:
        return "error";
      default:
        return "default";
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        width="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box textAlign="center" mt={5}>
        <Typography variant="h6" color="error">
          {error?.message || t("pharmacy.errorLoadingPrescriptions")}
        </Typography>
        <Button variant="contained" color="primary" onClick={() => refetch()}>
          {t("pharmacy.retry")}
        </Button>
      </Box>
    );
  }

  if (isSuccess) {
    const prescriptions = data?.results || [];

    return (
      <Grid container spacing={3} padding="20px" mt={3}>
        <Grid item xs={12}>
          <Box
            display="flex"
            flexDirection={isSmallScreen ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isSmallScreen ? "stretch" : "center"}
            mb={3}
            gap={isSmallScreen ? 2 : 0}
          >
            <Box display="flex" alignItems="center">
              <Typography
                variant="h4"
                fontWeight="bold"
                color="primary"
                sx={{ flex: isSmallScreen ? "none" : "1 1 auto" }}
              >
                {t("pharmacy.prescriptionList")}
              </Typography>
            </Box>

            {/* Add New Button */}
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/doctor/prescription/add")}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                paddingX: 3,
                paddingY: isSmallScreen ? 1 : 1.5,
                alignSelf: isSmallScreen ? "flex-end" : "center",
                borderRadius: "8px",
                boxShadow: 3,
                transition: "all 0.3s ease",
                backgroundColor: "#007174",
                "&:hover": {
                  backgroundColor: "#007174",
                  boxShadow: 6,
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  boxShadow: 2,
                  transform: "translateY(0px)",
                },
                "& .MuiButton-startIcon": {
                  marginRight: 1,
                },
              }}
            >
              {t("pharmacy.addNew")}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          {/* Search Bar */}
          <TextField
            variant="outlined"
            size="small"
            placeholder={t("pharmacy.search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              "aria-label": t("pharmacy.search"),
            }}
            sx={{
              flex: isSmallScreen ? "none" : "2 1 auto",
              width: isSmallScreen ? "100%" : "auto",
            }}
          />
        </Grid>

        {/* Existing Prescriptions */}
        {prescriptions.length > 0 ? (
          prescriptions.map((prescription, index) => {
            const isPaid = prescription.payment_status === 2;
            return (
              <Grid item xs={12} md={6} key={prescription.id}>
                <Paper
                  elevation={isPaid ? 2 : 4}
                  sx={{
                    mb: 3,
                    padding: "24px",
                    borderRadius: "12px",
                    width: "100%",
                    boxShadow: isPaid
                      ? "0px 4px 12px rgba(0, 0, 0, 0.1)"
                      : "0px 8px 24px rgba(255, 0, 0, 0.3)",
                    backgroundColor: isPaid ? "#f5f5f5" : "#fff9f5",
                    borderLeft: !isPaid ? "4px solid #ff1744" : "none",
                  }}
                >
                  {/* Prescription Header */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="primary"
                      sx={{ fontSize: isSmallScreen ? "1rem" : "1.25rem" }}
                    >
                      {t("pharmacy.prescription")} #{prescription.id} -{" "}
                      {prescription.date}
                    </Typography>
                    <Chip
                      label={getPaymentStatusLabel(prescription.payment_status)}
                      color={getPaymentStatusColor(prescription.payment_status)}
                      sx={{
                        fontWeight: "bold",
                        fontSize: isSmallScreen ? "0.8rem" : "0.9rem",
                      }}
                    />
                    <Stack direction="row" justifyContent="flex-end">
                      <Tooltip title={t("pharmacy.print_prescription")}>
                        <IconButton
                          color="primary"
                          onClick={() => handlePrintClick(prescription)}
                        >
                          <PrintIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>

                  {/* Prescription Info */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    sx={{ mt: 2 }}
                  >
                    <Typography
                      fontSize={isSmallScreen ? "0.85rem" : "1rem"}
                      color={!isPaid ? "error.main" : "text.primary"}
                    >
                      <strong>{t("pharmacy.doctor")}:</strong>{" "}
                      {prescription.doctor_display}
                    </Typography>
                    <Typography fontSize={isSmallScreen ? "0.85rem" : "1rem"}>
                      <strong>{t("pharmacy.patient")}:</strong>{" "}
                      {prescription.patient_display}
                    </Typography>
                    <Typography fontSize={isSmallScreen ? "0.85rem" : "1rem"}>
                      <strong>{t("pharmacy.total_price")}:</strong>{" "}
                      {prescription.total_price} ؋
                    </Typography>
                  </Stack>

                  {/* Action Buttons */}
                  {!isPaid && (
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      spacing={2}
                      sx={{ mt: 2 }}
                    >
                      <Tooltip title={t("pharmacy.delete_prescription")}>
                        <IconButton
                          color="error"
                          onClick={() =>
                            handleDeletePrescription(prescription.id)
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}

                  <Divider sx={{ mt: 2, mb: 2 }} />

                  {/* Accordion for Prescription Details (Medicines) */}
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel-${prescription.id}-content`}
                      id={`panel-${prescription.id}-header`}
                    >
                      <Typography
                        fontWeight="bold"
                        color={!isPaid ? "error.main" : "text.primary"}
                      >
                        {t("pharmacy.medicines")}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {prescription.drugs && prescription.drugs.length > 0 ? (
                        <Box sx={{ overflowX: "auto" }}>
                          <Table sx={{ minWidth: 650 }}>
                            <TableHead sx={{ backgroundColor: "#eef2f5" }}>
                              <TableRow>
                                <TableCell align="center">#</TableCell>
                                <TableCell>{t("pharmacy.category")}</TableCell>
                                <TableCell>{t("pharmacy.medicine")}</TableCell>
                                <TableCell>{t("pharmacy.dosage")}</TableCell>
                                <TableCell>
                                  {t("pharmacy.unitsPerMeal")}
                                </TableCell>
                                <TableCell>{t("pharmacy.quantity")}</TableCell>
                                <TableCell>{t("pharmacy.price")}</TableCell>
                                <TableCell>{t("pharmacy.purchasePrice")}</TableCell>
                                <TableCell>{t("pharmacy.notation")}</TableCell>
                                <TableCell>
                                  {t("pharmacy.is_before_meal")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("pharmacy.actions")}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {prescription.drugs.map((item, index) => (
                                <TableRow
                                  key={item.id}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "#f0f4f8",
                                    },
                                  }}
                                >
                                  <TableCell align="center">
                                    <Typography fontWeight="bold">
                                      {index + 1}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography>
                                      {item.stock_medicine_category_display}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography>
                                      {item.stock_medicine_display}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{item.dosage}</TableCell>
                                  <TableCell>{item.units_per_meal}</TableCell>
                                  <TableCell>{item.quantity}</TableCell>
                                  <TableCell>{item.price}</TableCell>
                                  <TableCell>{item.purchase_price}</TableCell>
                                  <TableCell>{item.notation}</TableCell>
                                  <TableCell>
                                    {item.is_before
                                      ? t("pharmacy.before")
                                      : t("pharmacy.after")}
                                  </TableCell>
                                  <TableCell align="center">
                                    {!isPaid && (
                                      <>
                                        <Tooltip
                                          title={t("pharmacy.edit_item")}
                                        >
                                          <IconButton
                                            color="primary"
                                            onClick={() => handleOpen(item)}
                                          >
                                            <EditIcon />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                          title={t("pharmacy.delete_item")}
                                        >
                                          <IconButton
                                            color="error"
                                            onClick={() =>
                                              handleDeletePrescriptionItem(
                                                item.id
                                              )
                                            }
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : (
                        <Typography>{t("pharmacy.no_medicines")}</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              </Grid>
            );
          })
        ) : (
          <Grid item xs={12}>
            <Box textAlign="center" mt={5}>
              <Typography variant="h6">
                {t("pharmacy.noMedicineOrder")}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Pagination Controls */}
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={10}
            mb={5}
          >
            <Box display="flex" alignItems="center">
              <Typography>{t("pharmacy.rowsPerPage")}: </Typography>
              <FormControl
                sx={{
                  ml: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
                size="small"
              >
                <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                  <MenuItem value={1000}>1000</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={Math.ceil(totalPrescriptions / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              color="primary"
            />
          </Box>
        </Grid>

        {/* Dialogs and Hidden Print Sections */}
        <ConfirmDialog />
        <ConfirmDialogItem />

        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>
            <Typography fontWeight="bold" color="primary">
              {t("pharmacy.edit_prescription_item")}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ padding: "20px" }}>
            {selectedItem && (
              <PrescriptionDetailsEdit
                prescriptionDetail={selectedItem}
                onClose={handleClose}
                token={token}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Print Section (Hidden) */}
        {printData && (
          <div ref={printRef} style={{ display: "none" }}>
            <PrescriptionPrint data={printData} />
          </div>
        )}
      </Grid>
    );
  }
};

export default PrescriptionList;
